<template>
  <b-card>
    <TitleTable titleTable="Comisiones" iconTable="fas fa-file-invoice-dollar" />
    <p class="text-muted">El sistema no paga. Revisa, exporta y marca como pagado. Fechas en MM-DD-YYYY.</p>
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">{{ errorMessage }}</b-alert>
    <b-alert v-if="notice" show variant="info" dismissible @dismissed="notice = null">{{ notice }}</b-alert>

    <b-row>
      <b-col md="3">
        <b-form-group label="Fecha de corte" label-for="cut-date">
          <b-form-input id="cut-date" v-model="cutDate" placeholder="MM-DD-YYYY" />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Estado" label-for="commission-status">
          <b-form-select id="commission-status" v-model="status" :options="statusOptions" />
        </b-form-group>
      </b-col>
      <b-col md="6" class="d-flex align-items-end flex-wrap">
        <b-button class="mr-2 mb-3" variant="primary" :disabled="loading" @click="loadReport">Ver reporte</b-button>
        <b-button class="mb-3" variant="outline-primary" :disabled="loading" @click="download">Exportar</b-button>
      </b-col>
    </b-row>

    <div v-if="report">
      <p v-if="totalsText">Totales: {{ totalsText }}</p>
      <h5>Agentes</h5>
      <b-table :items="agents" :fields="beneficiaryFields" small responsive show-empty empty-text="Sin agentes.">
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="openDetail('agent', data.item)">Detalle</b-button>
        </template>
      </b-table>
      <h5 class="mt-3">Agencias</h5>
      <p class="small text-muted">PatientFlow de agencias es 0.</p>
      <b-table :items="agencies" :fields="beneficiaryFields" small responsive show-empty empty-text="Sin agencias.">
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="openDetail('agency', data.item)">Detalle</b-button>
        </template>
      </b-table>
      <h5 class="mt-3">En espera por garantía</h5>
      <p v-if="onHold.rows">
        {{ onHold.rows }} comisiones retenidas por ${{ onHold.total }}
        <span v-if="onHold.subscriptionIds.length"> · suscripciones {{ onHold.subscriptionIds.join(', ') }}</span>
      </p>
      <p v-else class="text-muted">Nada retenido.</p>

      <h5 class="mt-4">Marcar como pagado</h5>
      <b-row>
        <b-col md="3">
          <b-form-input v-model="paidAt" placeholder="Fecha de pago MM-DD-YYYY" />
        </b-col>
        <b-col md="4">
          <b-button variant="success" :disabled="loading" @click="markPaid">Marcar pagado</b-button>
        </b-col>
      </b-row>
      <p v-if="paidSummary" class="mt-2">{{ paidSummary }}</p>
    </div>

    <div v-if="detailRows.length" class="mt-4">
      <h5>Detalle</h5>
      <b-table :items="detailRows" :fields="detailFields" small responsive />
    </div>

    <hr class="my-4" />
    <h5>Tarifas</h5>
    <b-button size="sm" variant="outline-secondary" class="mb-3" @click="loadRates">Recargar tarifas</b-button>
    <pre v-if="rates" class="small bg-light p-3">{{ ratesText }}</pre>

    <b-row>
      <b-col md="6">
        <h6>Nueva tarifa</h6>
        <b-form-input v-model="rate.memberships_parameters_id" class="mb-2" placeholder="ID del plan" />
        <b-form-select v-model="rate.beneficiary_type" class="mb-2" :options="beneficiaryOptions" />
        <b-form-input v-model="rate.effective_date" class="mb-2" placeholder="Vigente desde MM-DD-YYYY" />
        <b-form-input v-model="rate.amount" class="mb-2" placeholder="Monto" />
        <b-form-input v-model="rate.tiers" class="mb-2" placeholder="Tramos opcional 2:89, 3:125" />
        <b-form-input v-model="rate.extra_member" class="mb-2" placeholder="Extra por integrante" />
        <b-button variant="primary" @click="saveRate">Guardar tarifa</b-button>
      </b-col>
      <b-col md="6">
        <h6>Regla élite</h6>
        <b-form-select v-model="elite.beneficiary_type" class="mb-2" :options="beneficiaryOptions" />
        <b-form-input v-model="elite.unit" class="mb-2" placeholder="Unidad" />
        <b-form-input v-model="elite.threshold" class="mb-2" placeholder="Umbral" />
        <b-form-input v-model="elite.bonus" class="mb-2" placeholder="Bono" />
        <b-form-input v-model="elite.effective_date" class="mb-2" placeholder="MM-DD-YYYY" />
        <b-button variant="primary" class="mb-4" @click="saveElite">Guardar regla</b-button>

        <h6>PatientFlow</h6>
        <b-form-input v-model="flow.amount" class="mb-2" placeholder="Monto" />
        <b-form-input v-model="flow.excluded" class="mb-2" placeholder="Tipos de visita excluidos, separados por coma" />
        <b-form-input v-model="flow.effective_date" class="mb-2" placeholder="MM-DD-YYYY" />
        <b-button variant="primary" @click="saveFlow">Guardar tarifa PatientFlow</b-button>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BAlert, BButton, BCard, BCol, BFormGroup, BFormInput, BFormSelect, BRow, BTable } from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable'
import commissionsApi from '@/core/services/api/admin/consults/membershipCommissions'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import { unwrapList, unwrapRecord } from '@/core/utils/membershipFamily'

function parseTiers(text) {
  return String(text || '')
    .split(',')
    .map((part) => {
      const [members, amount] = part.split(':')
      return { members: Number(String(members || '').trim()), amount: Number(String(amount || '').trim()) }
    })
    .filter((tier) => tier.members > 0 && Number.isFinite(tier.amount))
}

/** Reporte, detalle, export y tarifas de comisiones. */
export default {
  name: 'MembershipCommissions',
  components: { BAlert, BButton, BCard, BCol, BFormGroup, BFormInput, BFormSelect, BRow, BTable, TitleTable },
  data() {
    return {
      cutDate: '',
      paidAt: '',
      status: 'pending',
      loading: false,
      errorMessage: null,
      notice: null,
      report: null,
      detailRows: [],
      paidSummary: '',
      rates: null,
      statusOptions: [
        { value: 'pending', text: 'Pendiente' },
        { value: 'paid', text: 'Pagado' },
      ],
      beneficiaryOptions: [
        { value: 'agent', text: 'Agente' },
        { value: 'agency', text: 'Agencia' },
      ],
      beneficiaryFields: [
        { key: 'name', label: 'Nombre' },
        { key: 'base', label: 'Base' },
        { key: 'elite', label: 'Élite' },
        { key: 'patient_flow', label: 'PatientFlow' },
        { key: 'clawback', label: 'Clawback' },
        { key: 'total', label: 'Total' },
        { key: 'actions', label: '' },
      ],
      detailFields: [
        { key: 'period_month', label: 'Mes' },
        { key: 'kind', label: 'Tipo' },
        { key: 'plan_name', label: 'Plan' },
        { key: 'payer_name', label: 'Pagador' },
        { key: 'company_name', label: 'Empresa' },
        { key: 'quantity', label: 'Cantidad' },
        { key: 'rate', label: 'Tarifa' },
        { key: 'amount', label: 'Monto' },
        { key: 'patient_name', label: 'Paciente' },
        { key: 'visit_date', label: 'Visita' },
        { key: 'type_visit', label: 'Tipo de visita' },
      ],
      rate: {
        memberships_parameters_id: '',
        beneficiary_type: 'agent',
        effective_date: '',
        amount: '',
        tiers: '',
        extra_member: '',
      },
      elite: { beneficiary_type: 'agent', unit: '', threshold: '', bonus: '', effective_date: '' },
      flow: { amount: '', excluded: '', effective_date: '' },
    }
  },
  computed: {
    agents() {
      return (this.report && this.report.agents) || []
    },
    agencies() {
      return (this.report && this.report.agencies) || []
    },
    // on_hold llega como { rows, total, subscription_ids }.
    onHold() {
      const held = (this.report && this.report.on_hold) || {}
      return {
        rows: Number(held.rows) || 0,
        total: held.total != null ? held.total : '0.00',
        subscriptionIds: Array.isArray(held.subscription_ids) ? held.subscription_ids : [],
      }
    },
    totalsText() {
      const totals = this.report && this.report.totals
      if (!totals || typeof totals !== 'object') return ''
      return Object.entries(totals).map(([key, value]) => `${key}: ${value}`).join(' · ')
    },
    ratesText() {
      return JSON.stringify(this.rates, null, 2)
    },
  },
  methods: {
    async loadReport() {
      if (!this.cutDate) {
        this.errorMessage = 'Escribe la fecha de corte en MM-DD-YYYY.'
        return
      }
      this.loading = true
      this.errorMessage = null
      try {
        const response = await commissionsApi.report({ cutDate: this.cutDate, status: this.status })
        this.report = unwrapRecord(response)
      } catch (error) {
        this.report = null
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar el reporte.')
      } finally {
        this.loading = false
      }
    },
    async openDetail(beneficiaryType, item) {
      this.errorMessage = null
      try {
        const response = await commissionsApi.detail({
          cutDate: this.cutDate,
          status: this.status,
          beneficiaryType,
          beneficiaryUserId: item.user_id,
        })
        this.detailRows = unwrapList(response)
      } catch (error) {
        this.detailRows = []
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar el detalle.')
      }
    },
    async download() {
      if (!this.cutDate) {
        this.errorMessage = 'Escribe la fecha de corte en MM-DD-YYYY.'
        return
      }
      this.loading = true
      this.errorMessage = null
      try {
        const response = await commissionsApi.exportFile({ cutDate: this.cutDate, status: this.status })
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `comisiones-${this.cutDate}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo exportar el reporte.')
      } finally {
        this.loading = false
      }
    },
    async markPaid() {
      if (!this.cutDate || !this.paidAt) {
        this.errorMessage = 'Fecha de corte y fecha de pago son obligatorias.'
        return
      }
      this.loading = true
      this.errorMessage = null
      try {
        const response = await commissionsApi.markPaid({ cutDate: this.cutDate, paidAt: this.paidAt })
        const record = unwrapRecord(response) || {}
        const paid = Array.isArray(record.paid) ? record.paid.length : 0
        const skipped = Array.isArray(record.skipped) ? record.skipped.length : 0
        this.paidSummary = `Marcados: ${paid}. Omitidos (incluye totales negativos): ${skipped}.`
        await this.loadReport()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo marcar como pagado.')
      } finally {
        this.loading = false
      }
    },
    async loadRates() {
      this.errorMessage = null
      try {
        const response = await commissionsApi.rates()
        this.rates = unwrapRecord(response)
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudieron cargar las tarifas.')
      }
    },
    async saveRate() {
      const tiers = parseTiers(this.rate.tiers)
      const payload = {
        memberships_parameters_id: Number(this.rate.memberships_parameters_id),
        beneficiary_type: this.rate.beneficiary_type,
        effective_date: this.rate.effective_date,
      }
      if (tiers.length) {
        payload.tiers = tiers
        if (this.rate.extra_member !== '') payload.extra_member = Number(this.rate.extra_member)
      } else {
        payload.amount = Number(this.rate.amount)
      }
      try {
        await commissionsApi.createRate(payload)
        this.notice = 'Tarifa creada.'
        await this.loadRates()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo crear la tarifa.')
      }
    },
    async saveElite() {
      try {
        await commissionsApi.createEliteRule({
          beneficiary_type: this.elite.beneficiary_type,
          unit: this.elite.unit,
          threshold: Number(this.elite.threshold),
          bonus: Number(this.elite.bonus),
          effective_date: this.elite.effective_date,
        })
        this.notice = 'Regla élite creada.'
        await this.loadRates()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo crear la regla élite.')
      }
    },
    async saveFlow() {
      const excluded = String(this.flow.excluded || '')
        .split(',')
        .map((item) => Number(item.trim()))
        .filter((item) => Number.isInteger(item) && item > 0)
      try {
        await commissionsApi.createPatientFlowRate({
          amount: Number(this.flow.amount),
          excluded_type_visit_ids: excluded,
          effective_date: this.flow.effective_date,
        })
        this.notice = 'Tarifa de PatientFlow creada.'
        await this.loadRates()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo crear la tarifa de PatientFlow.')
      }
    },
  },
}
</script>
